import { action, computed, flow, makeObservable, observable } from 'mobx';

import { PlanVersionsApi } from 'src/api/plan-versions';
import {
  addVersionToIntegrationRequest,
  copyCarpetVersion,
  createPrivateDraft,
  deleteCarpetVersion,
  toggleVersionForAnalyticsRequest,
} from 'src/api/version';
import { BaseApiError } from 'src/errors';
import { hasValue } from 'src/shared/utils/common';
import type { TPromiseController } from 'src/shared/utils/promise-controller';
import { createPromiseController } from 'src/shared/utils/promise-controller';
import type {
  TPlanVersion,
  TActualPlansGroups,
  TAnalyticsPlansGroup,
  TArchivedPlansGroup,
  TIntegrationPlansGroup,
} from 'src/store/comparison/types';
import { PlanVersionType } from 'src/store/comparison/types';
import { processActualData, getVersionsYearGroupsWithMonths } from 'src/store/comparison/utils';
import type { NotificationsStore } from 'src/store/notifications-store/notifications-store';

import {
  GOPLAN_DRILLING_CARPET_GRAPH_SELECT_PUBLIC_DRAFT_EDIT,
  GOPLAN_DRILLING_CARPET_GRAPH_SELECT_PUBLIC_DRAFT_READ,
  GOPLAN_DRILLING_CARPET_GRAPH_SELECT_PUBLISHED,
  GOPLAN_DRILLING_CARPET_GRAPH_SELECT_TEMP_DRAFT_READ,
  GOPLAN_GRAPH_SELECT_TEMP_DRAFT_EDIT,
  GOPLAN_GRAPH_SELECT_TEMP_DRAFT_EDIT_ALIAS,
} from '../roles-store/roles';
import type { RolesStore } from '../roles-store/roles-store';

export class PlanVersionStore {
  private readonly api = new PlanVersionsApi();
  private readonly notifications: NotificationsStore;
  private readonly rolesStore: RolesStore;
  private removeVersionController: TPromiseController<boolean> | null = null;

  @observable isConfirmRemoveVersionModalOpened: boolean = false;
  @observable isConfirmCopyVersionModalOpened: boolean = false;
  @observable isCopyVersionInProgress = false;
  @observable actualVersions: TPlanVersion[] = [];
  @observable archivedVersions: TPlanVersion[] = [];
  @observable analyticsVersions: TPlanVersion[] = [];
  @observable integrationVersions: TPlanVersion[] = [];
  @observable isLoading = false;
  @observable version?: TPlanVersion;
  @observable copyingVersion: TPlanVersion | null = null;
  integrationInProgressVersions = observable.map<number, TPlanVersion>();

  chosenAnalyticsVersions = observable.map<number, TPlanVersion>();

  constructor(notifications: NotificationsStore, rolesStore: RolesStore) {
    this.notifications = notifications;
    this.rolesStore = rolesStore;

    makeObservable(this);
  }

  @action.bound
  private initChosenAnalyticsVersions(): void {
    this.analyticsVersions.forEach((plan) => {
      if (plan.data.availableForDashboardLite) {
        this.chosenAnalyticsVersions.set(plan.id, plan);
      }
    });
  }

  @action.bound
  private openConfirmRemoveVersionModalOpened(): void {
    this.isConfirmRemoveVersionModalOpened = true;
  }
  @action.bound
  private closeConfirmRemoveVersionModalOpened(): void {
    this.isConfirmRemoveVersionModalOpened = false;
  }

  @flow.bound
  private async *copyVersion(parentId: number) {
    this.isCopyVersionInProgress = true;

    try {
      await copyCarpetVersion(parentId);
      await this.reloadVersions(false);
      yield;

      this.isConfirmRemoveVersionModalOpened = false;
      this.copyingVersion = null;
    } catch (e) {
      yield;

      if (e instanceof BaseApiError) {
        const message = e.message || e.responseMessage;

        if (message) {
          this.notifications.showErrorMessage(message);
        }
      }

      console.error(e);
      throw e;
    } finally {
      this.isCopyVersionInProgress = false;
    }
  }

  @computed
  get privateDrafts(): TPlanVersion[] {
    return this.actualData[1].plans;
  }

  @computed
  get isPersonalDraftCreationAvailable(): boolean {
    return this.privateDrafts.length < 5;
  }

  @action.bound
  setVersion(version: TPlanVersion): void {
    this.version = version;
  }

  @action.bound
  onCopyVersion(parentVersion: TPlanVersion): void {
    this.copyingVersion = parentVersion;

    this.isConfirmCopyVersionModalOpened = true;
  }

  @action.bound
  confirmCopyVersion(): void {
    if (!hasValue(this.copyingVersion)) {
      console.error('parent version is not presented');
      return;
    }

    this.copyVersion(this.copyingVersion.id);
  }

  @action.bound
  cancelCopyVersion(): void {
    this.copyingVersion = null;
    this.isConfirmCopyVersionModalOpened = false;
  }

  @action.bound
  confirmRemoveVersion(): void {
    this.removeVersionController?.resolve(true);
  }

  @action.bound
  cancelRemoveVersion(): void {
    this.removeVersionController?.resolve(false);
  }

  @flow.bound
  private async *loadVersions() {
    try {
      this.isLoading = true;

      const data: TPlanVersion[] = await this.api.getVersions();
      yield;

      this.actualVersions = data
        .filter((plan) => {
          if (plan.data.versionType === PlanVersionType.current) {
            if (!this.rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_GRAPH_SELECT_PUBLISHED])) {
              return false;
            }
          }

          if (plan.data.versionType === PlanVersionType.public) {
            if (
              !this.rolesStore.hasAccess([
                GOPLAN_DRILLING_CARPET_GRAPH_SELECT_PUBLIC_DRAFT_READ,
                GOPLAN_DRILLING_CARPET_GRAPH_SELECT_PUBLIC_DRAFT_EDIT,
              ])
            ) {
              return false;
            }
          }

          return !plan.data.isArchive && plan.status !== 'DELETED';
        })
        .sort(({ createdAt: firstCreatedAt }, { createdAt: secondCreatedAt }) => secondCreatedAt - firstCreatedAt);

      this.archivedVersions = (() => {
        if (
          !this.rolesStore.hasAccess([
            GOPLAN_DRILLING_CARPET_GRAPH_SELECT_TEMP_DRAFT_READ,
            GOPLAN_GRAPH_SELECT_TEMP_DRAFT_EDIT,
            GOPLAN_GRAPH_SELECT_TEMP_DRAFT_EDIT_ALIAS,
          ])
        ) {
          return [];
        }

        return data
          .filter((plan) => plan.data.isArchive && plan.status !== 'DELETED')
          .sort(({ createdAt: firstCreatedAt }, { createdAt: secondCreatedAt }) => secondCreatedAt - firstCreatedAt);
      })();

      this.analyticsVersions = [...this.actualVersions, ...this.archivedVersions].filter(
        (plan) => plan.data.versionType === PlanVersionType.current && plan.status !== 'DELETED',
      );

      this.integrationVersions = data
        .filter((plan) => plan.data.isIntegrated || plan.status === 'DELETED')
        .sort(({ createdAt: firstCreatedAt }, { createdAt: secondCreatedAt }) => secondCreatedAt - firstCreatedAt);

      this.initChosenAnalyticsVersions();

      // Set last actual public version by default.
      const defaultPlanVersion =
        this.actualVersions.find(({ data: { versionType } }) => versionType === PlanVersionType.public) ??
        this.actualVersions.find(({ data: { versionType } }) => versionType === PlanVersionType.current);

      if (defaultPlanVersion !== undefined) {
        this.version = defaultPlanVersion;
      } else {
        this.notifications.showErrorMessageT('errors:actualVersionNotFound');
      }
    } catch (error) {
      yield;

      console.error(error);
      this.notifications.showErrorMessageT('errors:failedToLoadVersions');
    } finally {
      this.isLoading = false;
    }
  }

  @flow.bound
  async *addVersionToIntegration(version: TPlanVersion) {
    this.integrationInProgressVersions.set(version.id, version);

    try {
      await addVersionToIntegrationRequest(version.id);

      this.notifications.showSuccessMessageT('notifications:successfulVersionIntegration');

      await this.reloadVersions(false);
      yield;
    } catch (e) {
      yield;
      if (e instanceof BaseApiError) {
        const message = e.message || e.responseMessage;

        if (message) {
          this.notifications.showErrorMessage(message);
        }
      }
    } finally {
      this.integrationInProgressVersions.delete(version.id);
    }
  }

  @flow.bound
  async *toggleVersionForAnalytics(version: TPlanVersion) {
    if (this.chosenAnalyticsVersions.has(version.id)) {
      try {
        await toggleVersionForAnalyticsRequest(version.id, false);
        await this.reloadVersions(false);
        yield;

        this.chosenAnalyticsVersions.delete(version.id);
      } catch (e) {
        yield;
        if (e instanceof BaseApiError) {
          const message = e.message || e.responseMessage;

          if (message) {
            this.notifications.showErrorMessage(message);
          }
        }
      }
      return;
    }

    try {
      await toggleVersionForAnalyticsRequest(version.id, true);
      await this.reloadVersions(false);
      yield;

      this.chosenAnalyticsVersions.set(version.id, version);
    } catch (e) {
      yield;
      if (e instanceof BaseApiError) {
        const message = e.message || e.responseMessage;

        if (message) {
          this.notifications.showErrorMessage(message);
        }
      }
    }
  }

  @flow.bound
  async *reloadVersions(isSwitchDefaultVersion: boolean = true) {
    try {
      const data: TPlanVersion[] = await this.api.getVersions();
      yield;

      this.actualVersions = data
        .filter((plan) => {
          if (plan.data.versionType === PlanVersionType.current) {
            if (!this.rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_GRAPH_SELECT_PUBLISHED])) {
              return false;
            }
          }

          if (plan.data.versionType === PlanVersionType.public) {
            if (
              !this.rolesStore.hasAccess([
                GOPLAN_DRILLING_CARPET_GRAPH_SELECT_PUBLIC_DRAFT_READ,
                GOPLAN_DRILLING_CARPET_GRAPH_SELECT_PUBLIC_DRAFT_EDIT,
              ])
            ) {
              return false;
            }
          }

          return !plan.data.isArchive && plan.status !== 'DELETED';
        })
        .sort(({ createdAt: firstCreatedAt }, { createdAt: secondCreatedAt }) => secondCreatedAt - firstCreatedAt);

      this.archivedVersions = (() => {
        if (!this.rolesStore.hasAccess([GOPLAN_DRILLING_CARPET_GRAPH_SELECT_TEMP_DRAFT_READ])) {
          return [];
        }

        return data
          .filter((plan) => plan.data.isArchive && plan.status !== 'DELETED')
          .sort(({ createdAt: firstCreatedAt }, { createdAt: secondCreatedAt }) => secondCreatedAt - firstCreatedAt);
      })();

      this.analyticsVersions = [...this.actualVersions, ...this.archivedVersions].filter(
        (plan) => plan.data.versionType === PlanVersionType.current && plan.status !== 'DELETED',
      );

      this.integrationVersions = data
        .filter((plan) => plan.data.isIntegrated || plan.status === 'DELETED')
        .sort(({ createdAt: firstCreatedAt }, { createdAt: secondCreatedAt }) => secondCreatedAt - firstCreatedAt);

      this.initChosenAnalyticsVersions();

      // Set last actual public version by default.
      const defaultPlanVersion = (() => {
        if (this.version) {
          return this.version;
        }

        return (
          this.actualVersions.find(({ data: { versionType } }) => versionType === PlanVersionType.public) ??
          this.actualVersions.find(({ data: { versionType } }) => versionType === PlanVersionType.current)
        );
      })();

      if (isSwitchDefaultVersion && defaultPlanVersion !== undefined) {
        this.version = defaultPlanVersion;
      } else if (defaultPlanVersion === undefined) {
        this.notifications.showErrorMessageT('errors:actualVersionNotFound');
      } else if (!isSwitchDefaultVersion && this.version) {
        const updatedCurrentVersion = data.find((version) => version.id === this.version?.id);

        if (updatedCurrentVersion) {
          this.version = updatedCurrentVersion;
        }
      }
    } catch (error) {
      yield;

      console.error(error);
      this.notifications.showErrorMessageT('errors:failedToLoadVersions');
    }
  }

  @flow.bound
  async *deleteVersionById(versionId: number) {
    try {
      this.removeVersionController = createPromiseController<boolean>();

      this.openConfirmRemoveVersionModalOpened();

      const isDeleteVersion = await this.removeVersionController;

      if (!isDeleteVersion) return;

      await deleteCarpetVersion(versionId);
      await this.reloadVersions(false);
      yield;
    } catch (e) {
      yield;
      console.error(e);
      throw e;
    } finally {
      this.closeConfirmRemoveVersionModalOpened();
    }
  }

  @flow.bound
  async *createPrivateDraft() {
    if (!hasValue(this.version)) {
      return;
    }

    try {
      const privateVersionId = await createPrivateDraft(this.version.id);

      await this.reloadVersions();
      yield;
      this.onVersionChange(privateVersionId);

      return privateVersionId;
    } catch (e) {
      yield;
      if (e instanceof BaseApiError && e.responseMessage) {
        this.notifications.showErrorMessage(e.responseMessage);
        return;
      }
      this.notifications.showErrorMessageT('errors:failedToCreatePrivateDraft');
      console.error(e);
    }
  }

  @flow.bound
  async *renameVersion(id: number, newName: string) {
    try {
      await this.api.renameVersion(id, newName);
      this.reloadVersions(false);
    } catch (e) {
      yield;
      this.reloadVersions(false);
      if (e instanceof BaseApiError && e.responseMessage) {
        this.notifications.showErrorMessage(e.responseMessage);
        return;
      }
      console.error(e);
    }
  }

  init = async (): Promise<void> => {
    await this.loadVersions();
  };

  @action.bound
  onVersionChange(versionId: number): void {
    for (const group of this.actualData) {
      for (const plan of group.plans) {
        if (plan.id === versionId) {
          this.version = plan;
          return;
        }
      }
    }
    for (const group of this.archivedData) {
      for (const monthGroup of group.months) {
        for (const plan of monthGroup.plans) {
          if (plan.id === versionId) {
            this.version = plan;
            return;
          }
        }
      }
    }
  }

  isVersionActualDraft(versionId: number): boolean {
    return !!this.actualVersions.find(
      (version) =>
        version.id === versionId &&
        [PlanVersionType.public, PlanVersionType.personal].includes(version.data.versionType),
    );
  }

  @computed
  get actualData(): [TActualPlansGroups, TActualPlansGroups] {
    return processActualData(this.actualVersions);
  }

  @computed
  get archivedData(): TArchivedPlansGroup[] {
    return getVersionsYearGroupsWithMonths('archived', this.archivedVersions);
  }

  @computed
  get analyticsData(): TAnalyticsPlansGroup[] {
    return getVersionsYearGroupsWithMonths('analytics', this.analyticsVersions);
  }

  @computed
  get integrationData(): TIntegrationPlansGroup[] {
    return getVersionsYearGroupsWithMonths('integration', this.integrationVersions);
  }

  @computed
  get publicDraft(): TPlanVersion | null {
    return this.actualVersions.find((plan) => plan.data.versionType === PlanVersionType.public) || null;
  }

  @computed
  get lastImportPlanVersionId(): number | undefined {
    const imports = this.actualVersions.filter(
      (item) => item.data.versionType === 'TEMP_DRAFT' && item.data.name.startsWith('import'),
    );

    return imports.at(-1)?.id;
  }
}
